import {
  FETCH_SETTINGS,
  FETCH_SETTINGS_SUCCESS,
  FETCH_SETTINGS_FAILED,
  EDIT_SETTINGS,
  CLEAR_SETTINGS_ERROR
} from "../store/types";

import store from '../store/store';
import { firebase } from '../config/configureFirebase';
import { get, update } from "firebase/database";
import { uploadBytesResumable, getDownloadURL } from "firebase/storage";

export const fetchSettings= () => async (dispatch) => {

  const {
    settingsRef
  } = firebase;

  dispatch({
    type: FETCH_SETTINGS,
    payload: null,
  });
  try {
    const snapshot = await get(settingsRef);
    if (snapshot.val()) {
      dispatch({
        type: FETCH_SETTINGS_SUCCESS,
        payload: snapshot.val(),
      });
    } else {
      dispatch({
        type: FETCH_SETTINGS_FAILED,
        payload: "Unable to fetch database and settings.",
      });
    }
  } catch (error) {
    dispatch({
      type: FETCH_SETTINGS_FAILED,
      payload: error.message,
    });
  }
};

export const editSettings = (settings) => async (dispatch) => {
  const {
    settingsRef
  } = firebase;
  // The two launch stamps (onboardingWizardSince / driverWelcomeCreditSince)
  // are written once, server-side, the first time their feature flag turns
  // on, and they decide which accounts count as "legacy" forever after. A
  // stale admin tab that loaded /settings before the stamp landed would
  // re-send its old value (or null) with the merge below and silently move
  // the legacy cut-off. So read the live node first and let an existing
  // stamp win over whatever this tab holds.
  // If the read fails (offline tab) fall back to what this tab loaded — the
  // pre-existing behaviour — rather than swallowing the save.
  let fresh = store.getState().settingsdata.settings || {};
  try {
    const freshSnap = await get(settingsRef);
    fresh = freshSnap.val() || {};
  } catch (e) {
    console.log('[editSettings] live /settings read failed, using loaded copy:', e && e.message);
  }
  // update (merge), not set (replace): the portal loads /settings once per
  // tab, so a stale admin tab saving with set() would wipe keys written
  // elsewhere in the meantime (other admins, the onboardingWizardSince
  // auto-stamp, server-side flags). Keys this tab never loaded are left
  // untouched by the merge.
  //
  // But a plain merge can never DELETE a key, and some tabs rely on that
  // (MileSettings drops meterRewardEveryTrips / airtimeDenominations /
  // redeemMileRate to fall back to defaults; the old whole-node set() did
  // this implicitly). So every key that was in the settings THIS tab loaded
  // but is absent from the object being saved is sent as null, which RTDB
  // treats as a delete inside update(). Both properties hold: intentional
  // removals land, and keys written elsewhere since load are preserved.
  const prev = store.getState().settingsdata.settings || {};
  const patch = Object.assign({}, settings);
  Object.keys(prev).forEach((k) => {
    if (!Object.prototype.hasOwnProperty.call(settings, k)) patch[k] = null;
  });
  ['onboardingWizardSince', 'driverWelcomeCreditSince'].forEach((k) => {
    if (fresh[k]) patch[k] = fresh[k];
  });
  // holidayPromoSince: the live stamp wins only while the campaign is
  // already ON in the database; a save that turns it on writes its own
  // fresh stamp (see GeneralSettings handleSubmit).
  if (fresh.holidayPromoSince && fresh.holidayPromoEnabled === true) patch.holidayPromoSince = fresh.holidayPromoSince;
  // Awaited: a refused or failed write reaches the tab's own error message
  // (each tab says "Settings updated" itself; the browser alert is gone).
  await update(settingsRef, patch);
  // The store follows the save. It used to keep the settings loaded at
  // sign-in, so the next tab saved from that stale copy and put the first
  // tab's old values back (owner, 28 Oct). It now holds what /settings holds
  // after this write: the live copy read above with the patch applied.
  const saved = Object.assign({}, fresh);
  Object.keys(patch).forEach((k) => {
    if (patch[k] === null) delete saved[k];
    else saved[k] = patch[k];
  });
  dispatch({
    type: EDIT_SETTINGS,
    payload: saved
  });
};

export const clearSettingsViewError = () => (dispatch) => {
  dispatch({
    type: CLEAR_SETTINGS_ERROR,
    payload: null
  });
};

// Admin-only /event_settings (live-event stream URL). Direct-ref helpers,
// no redux slice — only the portal Live Event tab reads/writes this node.
export const fetchEventSettings = async () => {
  const { eventSettingsRef } = firebase;
  const snapshot = await get(eventSettingsRef);
  return snapshot.val() || {};
};

export const editEventSettings = (data) => {
  const { eventSettingsRef } = firebase;
  return update(eventSettingsRef, data);
};

export const uploadEventImage = async (eventId, file) => {
  const { eventImageRef } = firebase;
  await uploadBytesResumable(eventImageRef(eventId), file);
  return getDownloadURL(eventImageRef(eventId));
};

export const fetchEventPayments = async () => {
  const { eventPaymentsRef } = firebase;
  const snapshot = await get(eventPaymentsRef);
  return snapshot.val() || {};
};